import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { LocationService } from '../instances/location';
import { IndicationMapConverter } from '@/converters';
import { GetIndicationMapAddressResponse } from '../response/location';

export const useIndicationMaps = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_INDICATION_MAPS', params],
    async () => {
      const [err, data] = await LocationService.getIndicationMap(params);
      if (err) throw err;
      return {
        data: IndicationMapConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

export const useIndicationMapById = () => {
  return useMutation({
    mutationKey: ['GET_INDICATION_MAP_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await LocationService.getIndicationMapById(uuid);
      if (err) {
        throw err;
      }
      return IndicationMapConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePostIndicationMapMutation = () => {
  return useMutation({
    mutationKey: ['POST_INDICATION_MAP'],
    mutationFn: async (payload: any) => {
      const [err, data] = await LocationService.postIndicationMap(payload);
      if (err) throw err;
      return IndicationMapConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutIndicationMapMutation = () => {
  return useMutation({
    mutationKey: ['PUT_INDICATION_MAP'],
    mutationFn: async ({ id, payload }: { id: string; payload: any }) => {
      const [err, data] = await LocationService.putIndicationMap(id, payload);
      if (err) throw err;
      return IndicationMapConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteIndicationMapMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_INDICATION_MAP'],
    mutationFn: async (id: string) => {
      const [err] = await LocationService.deleteIndicationMap(id);
      if (err) throw err;
    },
  });
};

export const useGetIndicationMapAddressByPostalCode = () => {
  return useMutation({
    mutationKey: ['GET_INDICATION_MAP_ADDRESS_BY_POSTAL_CODE'],
    mutationFn: async (postalCode: string): Promise<GetIndicationMapAddressResponse> => {
      const [err, data] = await LocationService.getIndicationMapAddressByPostalCode(postalCode);
      if (err) {
        throw err;
      }
      return data;
    },
  });
};
